"use client";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { forgotPasswordAction } from "@/actions/auth";
import { SimpleThemeToggle } from "@/components/theme-switcher";
import { BackgroundGradientAnimation } from "@/components/ui/background-gradient-animation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";

export default function ForgotPassword() {
  const t = useTranslations();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast({
        title: t("notifications.error"),
        description: t("forgotPassword.emailAddress"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await forgotPasswordAction(email);

      if (result.success) {
        setIsSubmitted(true);
      } else {
        toast({
          title: t("notifications.error"),
          description: result.error || t("notifications.genericError"),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t("notifications.error"),
        description: `${t("notifications.genericError")} ${error}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsLoading(true);

    try {
      const result = await forgotPasswordAction(email);

      if (result.success) {
        toast({
          title: t("notifications.success"),
          description: t("forgotPassword.checkEmail.resendEmail"),
        });
      } else {
        toast({
          title: t("notifications.error"),
          description: result.error || t("notifications.genericError"),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t("notifications.error"),
        description: `${t("notifications.genericError")} ${error}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BackgroundGradientAnimation>
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20">
        <SimpleThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-6 shadow-2xl">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                  {isSubmitted ? (
                    <CheckCircle2 className="w-8 h-8" />
                  ) : (
                    <Mail className="w-8 h-8" />
                  )}
                </div>
              </div>

              <h1 className="text-xl md:text-2xl font-bold text-center mb-2">
                {isSubmitted
                  ? t("forgotPassword.checkEmail.title")
                  : t("forgotPassword.title")}
              </h1>

              <p className="text-muted-foreground text-center mb-8">
                {isSubmitted ? (
                  <>
                    {t("forgotPassword.checkEmail.description")} {email}
                  </>
                ) : (
                  t("forgotPassword.subtitle")
                )}
              </p>

              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-foreground"
                    >
                      {t("forgotPassword.emailAddress")}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("forgotPassword.emailPlaceholder")}
                        className="pl-11 h-12"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    className="w-full py-5 font-semibold text-md"
                  >
                    {isLoading
                      ? t("forgotPassword.sending")
                      : t("forgotPassword.sendResetLink")}
                  </Button>
                </form>
              ) : (
                <>
                  <div className="bg-muted/50 rounded-lg p-4 mb-6 space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {t("forgotPassword.checkEmail.didntReceive")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("forgotPassword.checkEmail.linkExpires")}
                    </p>
                  </div>

                  <Button
                    type="button"
                    onClick={handleResendEmail}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full py-5 font-semibold"
                  >
                    {isLoading
                      ? t("forgotPassword.checkEmail.resending")
                      : t("forgotPassword.checkEmail.resendEmail")}
                  </Button>
                </>
              )}

              <div className="mt-6">
                <Link
                  href="/login"
                  className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t("forgotPassword.backToLogin")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </BackgroundGradientAnimation>
  );
}
